import { AlertCircle, Clock } from "lucide-react";
import { formatDate, daysUntil } from "@/lib/checklist";
import { Link } from "react-router-dom";

/**
 * AlertsPanel — surfaces expiring / expired documents & NMC checks due.
 * Works for both worker view (own) and reviewer view (all workers).
 */
export default function AlertsPanel({ alerts, showWorkerName = false }) {
  if (!alerts || alerts.length === 0) return null;
  const expired = alerts.filter((a) => a.expiry_status === "expired");
  const soon = alerts.filter((a) => a.expiry_status === "expiring_soon");

  return (
    <div data-testid="alerts-panel" className="rounded-xl border border-rose-200 bg-rose-50/60 p-5">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-rose-900">
            {expired.length > 0 && `${expired.length} expired`}
            {expired.length > 0 && soon.length > 0 && " · "}
            {soon.length > 0 && `${soon.length} expiring in ≤30 days`}
          </div>
          <ul className="mt-3 space-y-2">
            {alerts.slice(0, 8).map((a, idx) => (
              <li
                key={`${a.worker_id}-${a.item_key}-${idx}`}
                data-testid={`alert-${a.item_key}`}
                className="flex items-center justify-between gap-3 rounded-md bg-white border border-rose-100 px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{a.item_title}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
                    {showWorkerName && (
                      <Link to={`/workers/${a.worker_id}`} className="text-teal-700 font-semibold hover:underline">
                        {a.worker_name}
                      </Link>
                    )}
                    {showWorkerName && a.expiry_date && <span>·</span>}
                    {a.expiry_date && (
                      <>
                        <Clock className="h-3 w-3" />
                        <span>{a.kind === "nmc_check_due" ? "Check due" : `Expires ${formatDate(a.expiry_date)}`}</span>
                      </>
                    )}
                    {a.expiry_date && (
                      <>
                        <span>·</span>
                        <span>{describeDelta(a.expiry_date)}</span>
                      </>
                    )}
                    {a.kind === "nmc_check_due" && (
                      <span className="ml-1 rounded-full bg-teal-100 text-teal-700 px-1.5 py-0.5 text-[0.65rem] font-semibold">
                        NMC monthly check
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className={`shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold ${
                    a.expiry_status === "expired"
                      ? "bg-rose-100 text-rose-800 border-rose-300"
                      : "bg-amber-50 text-amber-800 border-amber-200"
                  }`}
                >
                  {a.expiry_status === "expired" ? "Expired" : "Expiring"}
                </span>
              </li>
            ))}
          </ul>
          {alerts.length > 8 && (
            <div className="text-xs text-slate-500 mt-2">+ {alerts.length - 8} more</div>
          )}
        </div>
      </div>
    </div>
  );
}

function describeDelta(iso) {
  const d = daysUntil(iso);
  if (d === null) return "";
  if (d < 0) return `${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"} ago`;
  if (d === 0) return "today";
  return `in ${d} day${d === 1 ? "" : "s"}`;
}
